// Structured, per-recipe metadata that the core recipe table doesn't carry: cuisine, heat,
// equipment and a visual description of the finished dish.
//
// The visual description is the single source for food imagery. Image prompts are built from it
// (scripts/images), and a generated image is only shown once a vision check confirms it matches
// (see recipeImages.ts). It also drives the honest placeholder when no verified image exists.

import { getRecipe, RECIPES, type Category } from './recipes';
import { generatedRecipes, subscribeGenerated } from './generated';
import type { CUISINES, EQUIPMENT } from './catalog/schema';

export type Cuisine = (typeof CUISINES)[number];

export type Equipment = (typeof EQUIPMENT)[number];

export interface RecipeVisual {
  /** What the plate is, in plain words. */
  dish: string;
  /** Ingredients a viewer should be able to see. */
  visible: string[];
  /** How it's cooked, as it shows on the plate. */
  method: string;
  /** Vessel and plating. */
  presentation: string;
  /** Three colours sampled from the real dish, darkest last. Used for placeholders. */
  palette: [string, string, string];
}

export interface RecipeMeta {
  cuisine: Cuisine;
  /** 0 = no heat, 1 = mild, 2 = noticeably spicy. Optional heat (e.g. chili flakes "o:") counts as 0. */
  heat: 0 | 1 | 2;
  equipment: Equipment[];
  visual: RecipeVisual;
}

export const CUISINE_LABELS: Record<Cuisine, string> = {
  american: 'American', chinese: 'Chinese', mexican: 'Mexican', italian: 'Italian', indian: 'Indian',
  mediterranean: 'Mediterranean', middle_eastern: 'Middle Eastern', asian: 'Pan-Asian', british: 'British',
  french: 'French', everyday: 'Everyday', japanese: 'Japanese', korean: 'Korean', thai: 'Thai',
  vietnamese: 'Vietnamese', european: 'European', latin_american: 'Latin American',
};

/** A broad preference also covers the specific cuisines under it ("Pan-Asian" includes Korean). */
const COVERS: Partial<Record<Cuisine, Cuisine[]>> = {
  asian: ['chinese', 'japanese', 'korean', 'thai', 'vietnamese'],
  mediterranean: ['middle_eastern'],
  mexican: ['latin_american'],
};

export function cuisineMatches(preferred: Cuisine[], cuisine: Cuisine): boolean {
  return preferred.some((p) => p === cuisine || (COVERS[p] ?? []).includes(cuisine));
}

/** Cuisines offered in onboarding (the ones the collection can actually serve). */
export const CUISINE_CHOICES: Cuisine[] = ['italian', 'mexican', 'chinese', 'asian', 'indian', 'mediterranean', 'american', 'french'];

/** Metadata is stored with each catalog entry. */
export const RECIPE_META: Record<string, RecipeMeta> = Object.fromEntries(RECIPES.map((r) => [r.id, r.meta]));

// Recipes Amari wrote on this device carry their own metadata too.
const addWritten = () => { for (const g of generatedRecipes()) RECIPE_META[g.recipe.id] ??= g.recipe.meta; };
addWritten();
subscribeGenerated(addWritten);

export function metaFor(recipeId: string): RecipeMeta {
  const m = RECIPE_META[recipeId];
  if (!m) throw new Error(`No metadata for recipe ${recipeId}`);
  return m;
}

/** Every recipe must have metadata; checked by tests and at module load in dev. */
export function missingMeta(): string[] {
  return RECIPES.filter((r) => !RECIPE_META[r.id]).map((r) => r.id);
}

export const CATEGORY_LABELS: Record<Category, string> = {
  american: 'American', european: 'European', east_asian: 'East Asian', south_southeast_asian: 'South and Southeast Asian',
  middle_east_north_africa: 'Middle East and North Africa', latin_american: 'Latin American',
  african_caribbean: 'African and Caribbean', international: 'Everyday international',
};

export function categoryOf(recipeId: string): Category | undefined {
  return getRecipe(recipeId)?.category;
}
